import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Anchor, Plane, Box, Zap, Globe2, ArrowUpRight, ArrowRight, Clock, MapPin, Shield, FileText, Truck, CheckCircle2 } from 'lucide-react';

const GlobalTech = () => {
  const [activeMode, setActiveMode] = useState('ocean');

  const oceanServices = [
    { type: 'FCL', title: 'Full Container Load', desc: '20\' and 40\' containers for large shipments. Door-to-port or door-to-door.', transit: '15-35 days' },
    { type: 'LCL', title: 'Less Than Container', desc: 'Consolidated ocean freight for smaller loads. Cost-effective for sub-container volumes.', transit: '20-40 days' },
  ];

  const airServices = [
    { type: 'Standard', title: 'Standard Air Freight', desc: 'Economical air option for time-sensitive cargo. 3-7 day transit.', transit: '3-7 days' },
    { type: 'Expedited', title: 'Expedited Air', desc: 'Priority handling with next-flight-out options. 1-3 day transit.', transit: '1-3 days' },
    { type: 'NFO', title: 'Next Flight Out', desc: 'Emergency air freight for critical shipments. Same or next day.', transit: 'Same Day' },
  ];

  const mockShipments = [
    { id: 'QFB-2024-0892', origin: 'Shanghai, CN', dest: 'Toronto, CA', mode: 'Ocean', status: 'In Transit', eta: 'Dec 15' },
    { id: 'QFB-2024-0893', origin: 'Frankfurt, DE', dest: 'Chicago, US', mode: 'Air', status: 'Cleared', eta: 'Dec 12' },
    { id: 'QFB-2024-0894', origin: 'Mumbai, IN', dest: 'Montreal, CA', mode: 'Ocean', status: 'At Port', eta: 'Dec 18' },
  ];

  const techFeatures = [
    { icon: MapPin, title: 'Real-Time GPS', desc: 'Track your freight to the meter' },
    { icon: FileText, title: 'Document Vault', desc: 'BOLs, customs, invoices — all in one place' },
    { icon: Shield, title: 'ELD Integration', desc: 'Driver hours, rest times synced' },
    { icon: Clock, title: 'Settlement Timer', desc: 'See exactly when payment clears' },
  ];

  const integrations = ['Project 44', 'FourKites', 'MacroPoint', 'Descartes', 'SAP', 'Oracle'];

  return (
    <div className="min-h-screen bg-bone">
      <section className="relative pt-32 pb-20 bg-forest overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-0 left-1/4 w-96 h-96 bg-amber/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-10 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.3, 1],
              y: [0, 20, 0]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs tracking-widest uppercase text-amber mb-4"
            >
              Global Forwarding
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-display-lg text-white mb-6"
            >
              Beyond the Border
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-body text-lg text-white/70 max-w-2xl"
            >
              Ocean and air freight forwarding with Quinte precision.
            </motion.p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">International Modes</div>
            <h2 className="font-display font-black text-display-lg text-forest">Ocean & Air</h2>
          </div>

          <div className="flex justify-center gap-4 mb-12">
            <button onClick={() => setActiveMode('ocean')} className={`flex items-center gap-3 px-8 py-4 rounded-xl font-display font-bold transition-all ${activeMode === 'ocean' ? 'bg-forest text-white' : 'bg-white text-forest border border-forest/10'}`}>
              <Anchor className="w-5 h-5" />Ocean
            </button>
            <button onClick={() => setActiveMode('air')} className={`flex items-center gap-3 px-8 py-4 rounded-xl font-display font-bold transition-all ${activeMode === 'air' ? 'bg-forest text-white' : 'bg-white text-forest border border-forest/10'}`}>
              <Plane className="w-5 h-5" />Air
            </button>
          </div>

          <motion.div key={activeMode} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid md:grid-cols-2 gap-8">
            {(activeMode === 'ocean' ? oceanServices : airServices).map((service, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-forest/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-forest rounded-xl flex items-center justify-center">
                    {activeMode === 'ocean' ? <Anchor className="w-6 h-6 text-amber" /> : <Plane className="w-6 h-6 text-amber" />}
                  </div>
                  <span className="font-mono text-sm font-bold text-forest bg-amber px-3 py-1.5 rounded-lg">{service.transit}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-forest mb-2">{service.title}</h3>
                <p className="font-body text-ink">{service.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-forest">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="font-mono text-xs tracking-widest uppercase text-amber mb-4">Technology Platform</div>
            <h2 className="font-display font-black text-display-lg text-white">Visibility Traditional Brokers Cannot Match</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-forest-2 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber rounded-lg flex items-center justify-center"><Box className="w-5 h-5 text-forest" /></div>
                <div>
                  <div className="font-display font-bold text-white">Client TMS Portal</div>
                  <div className="font-mono text-xs text-white/50">Live Tracking Dashboard</div>
                </div>
              </div>
              <div className="space-y-3">
                {mockShipments.map((ship, i) => (
                  <div key={i} className="bg-forest p-4 rounded-lg flex items-center justify-between">
                    <div>
                      <div className="font-mono text-xs text-amber">{ship.id}</div>
                      <div className="font-body text-sm text-white/70">{ship.origin} → {ship.dest}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-body text-sm text-white">{ship.status}</div>
                      <div className="font-mono text-xs text-white/50">ETA {ship.eta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-forest-2 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber rounded-lg flex items-center justify-center"><Truck className="w-5 h-5 text-forest" /></div>
                <div>
                  <div className="font-display font-bold text-white">Carrier Dashboard</div>
                  <div className="font-mono text-xs text-white/50">Driver & Fleet Management</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {techFeatures.map((feat, i) => (
                  <div key={i} className="bg-forest p-4 rounded-lg">
                    <feat.icon className="w-5 h-5 text-amber mb-2" />
                    <div className="font-body text-sm text-white font-semibold">{feat.title}</div>
                    <div className="font-body text-xs text-white/60">{feat.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display font-black text-display-lg text-forest mb-6">Ready to Go Global?</h2>
          <a href="/quote" className="btn-primary text-lg px-10 py-5">Request International Quote<ArrowRight className="w-6 h-6" /></a>
        </div>
      </section>
    </div>
  );
};

export default GlobalTech;
